/**
 * Row schemas for every DB table. Plain `Schema.Struct` — they
 * compose with `@effect/sql`'s `SqlSchema.findAll` / `single` / `void`
 * codecs directly. No Kysely-style triple-wrappers
 * (Selectable/Insertable/Updateable) — `@effect/sql` covers the same
 * use cases via `Schema.optional` + decode contexts, without the
 * extra ColumnType machinery.
 *
 * Generated columns (e.g. `id` produced by `gen_random_uuid()` default,
 * `created_at` defaulted to `NOW()`) are typed as `Schema.optional`
 * here. The decoder on a SELECT still gets the populated value; an
 * INSERT can omit the field and let Postgres fill it in.
 *
 * Per PLAN.md "Quotas & limits" + "Plans" + "Identity & auth":
 *   - `plan`       — lookup table of plan tiers + limits
 *   - `account`    — one row per user (from openauth); free vs paid
 *   - `site`       — one row per site; FK to `account`
 *   - `site_quota` — per-site usage counters (sites count, asset bytes)
 *   - `site_refs`  — git refs (v2 — see S2 spike fallback path); v1
 *                    uses in-process mutex inside workerd-runtime
 */
import * as Schema from "effect/Schema";

import { SiteId } from "./ids.js";

// ----------------------------------------------------------------- plan

export const PlanTier = Schema.Literal("free", "pro");
export type PlanTier = Schema.Schema.Type<typeof PlanTier>;

export const PlanRow = Schema.Struct({
  tier: PlanTier,
  maxSites: Schema.Int.pipe(Schema.positive()),
  maxAssetBytesPerSite: Schema.Int.pipe(Schema.positive()),
  maxVersionsRetained: Schema.Int.pipe(Schema.positive()),
});
export type PlanRow = Schema.Schema.Type<typeof PlanRow>;

// ----------------------------------------------------------------- account

export const AccountRow = Schema.Struct({
  id: Schema.optional(Schema.UUID),
  openauthSubject: Schema.String,
  email: Schema.String,
  planTier: PlanTier,
  createdAt: Schema.optional(Schema.DateFromSelf),
});
export type AccountRow = Schema.Schema.Type<typeof AccountRow>;

// ----------------------------------------------------------------- site

export const SiteRow = Schema.Struct({
  id: SiteId,
  accountId: Schema.UUID,
  name: Schema.String.pipe(Schema.minLength(1)),
  slug: Schema.String, // platform subdomain (PLAN.md "Slugs & lifecycle")
  createdAt: Schema.optional(Schema.DateFromSelf),
  deletedAt: Schema.NullOr(Schema.DateFromSelf),
});
export type SiteRow = Schema.Schema.Type<typeof SiteRow>;

// ----------------------------------------------------------------- site_quota

export const SiteQuotaRow = Schema.Struct({
  siteId: SiteId,
  assetBytes: Schema.Int.pipe(Schema.nonNegative()),
  pushCount: Schema.Int.pipe(Schema.nonNegative()),
  versionCount: Schema.Int.pipe(Schema.nonNegative()),
  updatedAt: Schema.optional(Schema.DateFromSelf),
});
export type SiteQuotaRow = Schema.Schema.Type<typeof SiteQuotaRow>;

// ----------------------------------------------------------------- site_refs (v2)

/**
 * Per the S2 spike fallback: garage v2.1.0 doesn't honor S3 conditional
 * PUT, so v1 uses an in-process mutex in workerd-runtime for ref CAS.
 * v2 (multi-pod) migrates refs to this table with `SELECT … FOR UPDATE`.
 * Defined here so the schema is ready when v2 lands; the table is NOT
 * created in v1.
 */
export const SiteRefRow = Schema.Struct({
  siteId: SiteId,
  refname: Schema.String, // e.g. "refs/heads/main"
  oid: Schema.String.pipe(Schema.pattern(/^[0-9a-f]{40}$/)), // git sha1
  updatedAt: Schema.optional(Schema.DateFromSelf),
});
export type SiteRefRow = Schema.Schema.Type<typeof SiteRefRow>;
